'use client';
import React, { useState } from 'react';
import { X, Ban, CheckCircle } from 'lucide-react';

type BlockState = 'confirm' | 'blocking' | 'blocked';

interface BlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName?: string;
}

export default function BlockModal({ isOpen, onClose, userName = 'this resident' }: BlockModalProps) {
  const [state, setState] = useState<BlockState>('confirm');
  const [undone, setUndone] = useState(false);

  if (!isOpen) return null;

  function handleBlock() {
    setState('blocking');
    setTimeout(() => setState('blocked'), 1000);
  }

  function handleUndo() {
    setUndone(true);
    setTimeout(() => {
      setUndone(false);
      setState('confirm');
      onClose();
    }, 800);
  }

  function handleClose() {
    setState('confirm');
    setUndone(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        className="absolute inset-0 backdrop-blur-sm"
        style={{ background: 'rgba(7,6,12,0.80)' }}
        onClick={state === 'blocked' ? handleClose : undefined}
      />
      <div
        className="relative w-full max-w-sm rounded-2xl shadow-2xl"
        style={{
          background: '#0C0A16',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.60)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5 border-b"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(167,163,196,0.08)', border: '1px solid rgba(167,163,196,0.15)' }}
            >
              <Ban size={14} style={{ color: '#A7A3C4' }} />
            </div>
            <h2 className="font-display font-semibold text-[15px]" style={{ color: '#F2F4FF' }}>
              {state === 'blocked' ? 'Resident blocked' : 'Block resident'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg transition-all duration-150 hover:bg-white/[0.06] active:scale-95"
            style={{ color: '#A7A3C4' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {state === 'blocked' ? (
            <div className="flex flex-col items-center text-center">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ background: 'rgba(45,212,160,0.08)', border: '1px solid rgba(45,212,160,0.20)' }}
              >
                <CheckCircle size={24} style={{ color: '#2DD4A0' }} />
              </div>
              <h3 className="font-display font-semibold text-[16px] mb-2" style={{ color: '#F2F4FF' }}>
                {undone ? 'Unblocking…' : `${userName} is blocked`}
              </h3>
              <p className="text-[13.5px] leading-relaxed mb-6" style={{ color: '#A7A3C4' }}>
                Their content will no longer appear in your view. This is a personal visibility setting — moderation access is unaffected.
              </p>
              <div className="flex gap-3 w-full">
                <button onClick={handleClose} className="btn-primary flex-1 py-2.5 text-[13.5px]">
                  Done
                </button>
                <button
                  onClick={handleUndo}
                  className="btn-secondary px-4 py-2.5 text-[13px]"
                  disabled={undone}
                >
                  Undo
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-[14px] leading-relaxed mb-5" style={{ color: '#A7A3C4' }}>
                Blocking <span style={{ color: '#F2F4FF' }}>{userName}</span> will hide their content from your view across the platform.
              </p>

              <div
                className="p-4 rounded-xl mb-6 space-y-2"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                {[
                  'Their posts and profile will be hidden from you',
                  'They will not be notified',
                  'Moderation access is unaffected',
                  'You can undo this at any time',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <div className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: 'rgba(167,163,196,0.40)' }} />
                    <span className="text-[13px]" style={{ color: 'rgba(167,163,196,0.75)' }}>{item}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleBlock}
                  disabled={state === 'blocking'}
                  className="btn-secondary flex-1 py-2.5 text-[13.5px]"
                  style={{ borderColor: 'rgba(167,163,196,0.20)', color: '#A7A3C4' }}
                >
                  {state === 'blocking' ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                      Blocking…
                    </span>
                  ) : 'Block resident'}
                </button>
                <button onClick={handleClose} className="btn-primary flex-1 py-2.5 text-[13.5px]">
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
